'use client'

import { useEffect, useState } from 'react'

import './rock-number-ticker.css'

/**
 * Illustrates the free matching Rock That Number offer: a house number
 * rolling into place like it's being set on the boundary wall. No real photo
 * of a Coldstream Rock That Number exists yet, so this stands in for one.
 * Digits scramble then settle, split-flap style, on a random two-digit number
 * (10 to 99), re-rolling periodically while the section sits idle.
 */

const ROLL_INTERVAL = 6000

// 10 to 99 inclusive. Client-only, so it never runs during SSR.
function randomNumber() {
  return 10 + Math.floor(Math.random() * 90)
}

function TickerDigit({
  targetValue,
  settleAt,
  rollKey,
}: {
  targetValue: number
  settleAt: number
  rollKey: number
}) {
  const [displayed, setDisplayed] = useState(targetValue)
  const [settled, setSettled] = useState(true)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduced) {
      const id = requestAnimationFrame(() => {
        setDisplayed(targetValue)
        setSettled(true)
      })
      return () => cancelAnimationFrame(id)
    }
    let counter = Math.floor(Math.random() * 10)
    let tick = 0
    let settle = 0
    const raf = requestAnimationFrame(() => {
      setSettled(false)
      setDisplayed(counter)
      tick = window.setInterval(() => {
        counter += 1
        setDisplayed(counter % 10)
      }, 65)
      settle = window.setTimeout(() => {
        window.clearInterval(tick)
        setDisplayed(targetValue)
        setSettled(true)
      }, settleAt)
    })
    return () => {
      cancelAnimationFrame(raf)
      window.clearInterval(tick)
      window.clearTimeout(settle)
    }
    // rollKey forces a fresh scramble even when a re-roll lands the same digit.
  }, [targetValue, settleAt, rollKey])

  return (
    <div className={`rnt-digit ${settled ? 'rnt-settled' : 'rnt-scrambling'}`}>
      <span>{displayed}</span>
    </div>
  )
}

export function RockNumberTicker() {
  // Null on the server and the first client render so the markup matches and
  // hydration stays clean; the real random number lands after mount.
  const [num, setNum] = useState<number | null>(null)
  const [rollKey, setRollKey] = useState(0)

  useEffect(() => {
    const roll = () => {
      setNum(randomNumber())
      setRollKey((k) => k + 1)
    }
    roll()
    const id = window.setInterval(roll, ROLL_INTERVAL)
    return () => window.clearInterval(id)
  }, [])

  const digits = (num ?? 10).toString().padStart(2, '0').split('').map(Number)

  return (
    <div
      className="rnt-plaque"
      role="img"
      aria-label="House number carved into matching Coldstream stone"
    >
      <div className="rnt-digits">
        {digits.map((d, i) => (
          <TickerDigit key={i} targetValue={d} settleAt={700 + i * 260} rollKey={rollKey} />
        ))}
      </div>
      <div className="rnt-plate">YOUR NUMBER</div>
    </div>
  )
}
